import React from 'react';
import { css } from '@emotion/react';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import type { Options } from '@contentful/rich-text-react-renderer';
import { SPACER } from 'radiance-ui/lib/constants';
import { ContentfulImage } from 'components/global/ContentfulImage';

const containerStyles = css`
  gap: ${SPACER.small};
  display: flex;
  flex-direction: column;
`;

const paragraphStyles = css`
  color: inherit;
  font-size: inherit;
  line-height: inherit;
`;

const unorderedListStyles = css`
  padding-left: ${SPACER.xlarge};
  list-style-type: disc;
`;

const orderedListStyles = css`
  padding-left: ${SPACER.xlarge};
`;

const caslonStyles = css`
  font-family: 'big-caslon-fb', Georgia, 'Times New Roman', serif;
  font-weight: 400;
  font-style: normal;

  /*
   * A fragment can carry the bold mark too, and the renderer nests marks in
   * array order, so the b can land either side of this span. The kit ships a
   * single 400 face, so let the declared weight win in both nestings rather
   * than have the browser synthesize a bold that does not exist.
   */
  b,
  strong {
    font-weight: inherit;
  }
`;

export const RICH_TEXT_OPTIONS: Options = {
  renderNode: {
    [BLOCKS.DOCUMENT]: (_node, children) => (
      <div css={containerStyles}>{children}</div>
    ),
    [BLOCKS.PARAGRAPH]: (_node, children) => (
      <p css={paragraphStyles}>{children}</p>
    ),
    [BLOCKS.OL_LIST]: (_node, children) => (
      <ol css={orderedListStyles}>{children}</ol>
    ),
    [BLOCKS.UL_LIST]: (_node, children) => (
      <ul css={unorderedListStyles}>{children}</ul>
    ),
    [INLINES.HYPERLINK]: ({ data }, children) => (
      <a href={data.uri}>{children}</a>
    ),
    [BLOCKS.EMBEDDED_ASSET]: ({ data }) => (
      <ContentfulImage image={data.target} />
    ),
  },
};

// Headline-scoped rich text options: reuses the ITALIC mark to set a fragment
// in the Big Caslon Typekit face instead of ordinary italics. RICH_TEXT_OPTIONS
// is spread, never mutated — it has no renderMark today, so nothing is clobbered.
export const HEADLINE_RICH_TEXT_OPTIONS: Options = {
  ...RICH_TEXT_OPTIONS,
  renderMark: {
    [MARKS.ITALIC]: (text) => <span css={caslonStyles}>{text}</span>,
  },
};
